import { Component } from '@verndale/core';
import { isDevice } from '../helpers';

class Module extends Component {
  setupDefaults() {
    this.options = [...this.el];
    this.style = this.el.dataset.style;

    this.buildDropdown();
  }

  addListeners() {
    document.addEventListener('click', this.handleClickOutside.bind(this));
    this.el.addEventListener('change', this.handleOptionSelect.bind(this));
    this.dom.trigger.addEventListener(
      'click',
      this.handleTriggerClick.bind(this)
    );
    this.dom.trigger.addEventListener(
      'keydown',
      this.handleTriggerKeydown.bind(this)
    );
    this.dom.trigger.parentNode.addEventListener(
      'blur',
      this.hideDropdownList.bind(this)
    );
    this.dom.list.childNodes.forEach(item => {
      item.addEventListener('click', () => this.selectItem(item));
    });
  }

  on(event, callback) {
    this.el.addEventListener(event, callback);
  }

  setValue(value) {
    let event;

    if (typeof Event === 'function') {
      event = new Event('change');
    } else {
      event = document.createEvent('Event');
      event.initEvent('change', true, true);
    }

    this.el.value = value;
    this.el.dispatchEvent(event);
  }

  getValue() {
    return this.el.value;
  }

  handleClickOutside(e) {
    if (this.dom.wrapper !== e.target && !this.dom.wrapper.contains(e.target)) {
      this.hideDropdownList();
    }
  }

  handleOptionSelect() {
    let event;

    if (typeof Event === 'function') {
      event = new Event('option');
    } else {
      event = document.createEvent('Event');
      event.initEvent('option', true, true);
    }

    this.dom.trigger.innerHTML = this.options.find(
      option => option.value === this.el.value
    ).innerHTML;
    this.options.forEach(option => {
      if (option.value === this.el.value) {
        option.setAttribute('aria-selected', true);
      } else {
        option.setAttribute('aria-selected', false);
      }
    });

    this.dom.list.childNodes.forEach(item => {
      if (item.dataset.value === this.el.value) {
        item.setAttribute('aria-selected', true);
      } else {
        item.setAttribute('aria-selected', false);
      }
    });

    this.el.dispatchEvent(event);
  }

  handleTriggerClick() {
    // console.log(isDevice());
    // if (isDevice()) {
    //   return;
    // }

    if (!this.dom.trigger.getAttribute('aria-expanded')) {
      this.showDropdownList();
    } else {
      this.hideDropdownList();
    }
  }

  handleTriggerKeydown(e) {
    if (isDevice()) return;

    const key = e.which || e.keyCode;

    /* eslint-disable indent */
    switch (key) {
      case 13: // ENTER
        if (this.dom.trigger.getAttribute('aria-expanded')) {
          if (this.activeItem) {
            e.preventDefault();
            e.stopPropagation();
            this.selectItem(this.activeItem);
          }
        }
        break;
      case 38: // UP
        e.preventDefault();
        this.showDropdownList();

        if (this.activeItem) {
          const nextElement = this.activeItem.previousElementSibling;

          if (nextElement) {
            this.focusItem(nextElement);
          } else {
            this.focusLast();
          }
        } else {
          this.focusLast();
        }

        break;
      case 40: // DOWN
        e.preventDefault();
        this.showDropdownList();

        if (this.activeItem) {
          const nextElement = this.activeItem.nextElementSibling;

          if (nextElement) {
            this.focusItem(nextElement);
          } else {
            this.focusFirst();
          }
        } else {
          this.focusFirst();
        }

        break;
      case 36:
        e.preventDefault();
        this.focusFirst();
        break;
      case 35:
        e.preventDefault();
        this.focusLast();
        break;
    }
    /* eslint-enable indent */
  }

  selectItem(item) {
    this.el.value = item.dataset.value;
    this.handleOptionSelect();
    this.hideDropdownList();
  }

  defocusItem(element) {
    if (!element) return;
    element.removeAttribute('aria-selected');
  }

  focusItem(element) {
    this.defocusItem(this.activeItem);
    element.setAttribute('aria-selected', 'true');
    this.activeItem = element;

    if (this.dom.list.scrollHeight > this.dom.list.clientHeight) {
      const scrollBottom = this.dom.list.clientHeight + this.dom.list.scrollTop;
      const elementBottom = element.offsetTop + element.offsetHeight;
      if (elementBottom > scrollBottom) {
        this.dom.list.scrollTop = elementBottom - this.dom.list.clientHeight;
      } else if (element.offsetTop < this.dom.list.scrollTop) {
        this.dom.list.scrollTop = element.offsetTop;
      }
    }
  }

  focusFirst() {
    const firstItem = this.dom.list.querySelector('[role="option"]');
    if (firstItem) {
      this.focusItem(firstItem);
    }
  }

  focusLast() {
    const items = this.dom.list.querySelectorAll('[role="option"]');

    if (items.length) {
      this.focusItem(items[items.length - 1]);
    }
  }

  showDropdownList() {
    this.dom.trigger.setAttribute('aria-expanded', true);
  }

  hideDropdownList() {
    this.dom.trigger.removeAttribute('aria-expanded');
    this.defocusItem(this.activeItem);
    this.activeItem = null;
  }

  getPlaceholder() {
    const placeholder = this.options.filter(option => !option.value);
    return placeholder.length ? placeholder[0].text : '';
  }

  buildDropdown() {
    this.dom.wrapper = document.createElement('div');
    this.el.setAttribute('aria-hidden', true);
    this.el.setAttribute('tabindex', '-1');
    this.el.parentNode.insertBefore(this.dom.wrapper, this.el);
    this.dom.wrapper.classList.add('dropdown');

    if (this.style) {
      this.dom.wrapper.classList.add(`dropdown--${this.style}`);
    }

    if (isDevice()) {
      this.dom.wrapper.classList.add('dropdown--native');
    }
    this.dom.wrapper.appendChild(this.el);

    this.dom.trigger = document.createElement('button');
    this.dom.trigger.setAttribute('type', 'button');
    this.dom.trigger.setAttribute('role', 'combobox');
    this.dom.trigger.setAttribute('aria-haspopup', 'listbox');
    this.dom.trigger.setAttribute(
      'aria-label',
      this.el.getAttribute('aria-label')
    );
    this.dom.trigger.classList.add('dropdown__trigger');

    if (this.el.value) {
      const selectedOption = this.options.find(
        option => option.value === this.el.value
      );

      if (selectedOption) {
        this.dom.trigger.innerHTML = selectedOption.text;
      } else {
        this.dom.trigger.innerHTML = this.getPlaceholder();
      }
    } else {
      this.dom.trigger.innerHTML = this.getPlaceholder();
    }

    this.dom.wrapper.appendChild(this.dom.trigger);

    this.dom.list = document.createElement('ul');
    this.dom.list.classList.add('dropdown__list');
    this.dom.list.setAttribute('role', 'listbox');
    this.dom.list.setAttribute(
      'aria-label',
      this.el.getAttribute('aria-label')
    );
    this.dom.list.innerHTML = this.options
      // .filter(option => option.value)
      .map(
        option =>
          `<li class="dropdown__item" role="option" data-value="${
            option.value
          }" aria-selected="${option.value === this.el.value}">${
            option.text
          }</li>`
      )
      .join('');
    this.el.parentNode.insertBefore(this.dom.list, this.el.nextSibling);
    this.dom.wrapper.appendChild(this.dom.list);
  }

  destroy() {
    const parent = this.el.parentNode;
    parent.removeChild(this.el);
    parent.parentNode.insertBefore(this.el, parent);
    parent.parentNode.removeChild(parent);
  }

  update() {
    this.destroy();
    this.setupDefaults();
    this.addListeners();
  }
}

export default Module;
